// Export an die Lohnabrechnung (DATEV Lodas — Bewegungsdaten).
//
// Lodas erwartet je Zeile: Personalnummer, Lohnart, Wert, Abrechnungsmonat.
// Die Lohnarten-NUMMERN sind je Kanzlei verschieden und werden deshalb gepflegt
// (Tabelle `lohnarten`); ohne Nummer bleibt die Zeile weg, statt eine falsche zu raten.
//
// WICHTIG: Vor dem ersten echten Import gehört diese Datei einmal mit dem
// Steuerberater gegengeprüft — Satzaufbau und Versionskennung unterscheiden sich
// zwischen Lodas und „Lohn und Gehalt".
import { csvZeile } from './zuschlaege';

export type LohnartSchluessel =
  | 'grundstunden' | 'ueberstunden' | 'nacht' | 'sonntag' | 'feiertag'
  | 'urlaub' | 'krank' | 'auszahlung';

export interface Lohnart {
  schluessel: LohnartSchluessel;
  nummer: string | null;
  bezeichnung: string;
  aktiv: boolean;
}

export interface LohnZeile {
  personalnummer: string | null;
  name: string;
  /** Werte je Lohnart: Stunden (dezimal) oder Tage */
  werte: Partial<Record<LohnartSchluessel, number>>;
}

/** Minuten → Dezimalstunden mit zwei Nachkommastellen (7:30 h → 7,50). */
export function dezimalStunden(minuten: number): number {
  return Math.round((minuten / 60) * 100) / 100;
}

export interface ExportErgebnis {
  inhalt: string;
  zeilen: number;
  /** Lohnarten ohne gepflegte Nummer — sie fehlen im Export */
  ohneNummer: string[];
  /** Mitarbeitende ohne Personalnummer — sie fehlen im Export */
  ohnePersonalnummer: string[];
}

/**
 * Baut die Lodas-Bewegungsdaten. `monat` im Format 'YYYY-MM'.
 * Jede Kombination aus Person und Lohnart mit einem Wert ungleich null ergibt eine Zeile.
 */
export function baueLodasExport(args: {
  monat: string;
  zeilen: LohnZeile[];
  lohnarten: Lohnart[];
  beraternummer?: string | null;
  mandantennummer?: string | null;
}): ExportErgebnis {
  const { monat, zeilen, lohnarten } = args;
  const nummerVon = new Map<string, string>();
  const ohneNummer: string[] = [];
  for (const la of lohnarten) {
    if (!la.aktiv) continue;
    if (la.nummer?.trim()) nummerVon.set(la.schluessel, la.nummer.trim());
    else ohneNummer.push(la.bezeichnung);
  }

  const [jahr, mm] = monat.split('-');
  const abrechnungsmonat = `${mm}${jahr}`; // Lodas erwartet MMJJJJ

  const kopf = [
    csvZeile(['[Allgemein]']),
    csvZeile(['Ziel', 'LODAS']),
    csvZeile(['Version_SST', '1.0']),
    csvZeile(['BeraterNr', args.beraternummer || '']),
    csvZeile(['MandantenNr', args.mandantennummer || '']),
    csvZeile(['Datumsformat', 'TT.MM.JJJJ']),
    csvZeile(['Stringbegrenzer', '"']),
    csvZeile(['']),
    csvZeile(['[Bewegungsdaten]']),
    csvZeile(['Pers-Nr', 'Lohnart', 'Wert', 'Abrechnungsmonat', 'Bezeichnung']),
  ];

  const ohnePersonalnummer: string[] = [];
  const daten: string[] = [];
  for (const z of zeilen) {
    if (!z.personalnummer?.trim()) { ohnePersonalnummer.push(z.name); continue; }
    for (const [schluessel, wert] of Object.entries(z.werte) as [LohnartSchluessel, number][]) {
      if (!wert) continue; // 0 und undefined nicht exportieren
      const nummer = nummerVon.get(schluessel);
      if (!nummer) continue;
      const bezeichnung = lohnarten.find((l) => l.schluessel === schluessel)?.bezeichnung || schluessel;
      daten.push(csvZeile([z.personalnummer.trim(), nummer, wert, abrechnungsmonat, bezeichnung]));
    }
  }

  return {
    // BOM voran, sonst zerlegt Excel die Umlaute; Zeilenende CRLF wie von DATEV erwartet.
    inhalt: '﻿' + [...kopf, ...daten].join('\r\n'),
    zeilen: daten.length,
    ohneNummer,
    ohnePersonalnummer: [...new Set(ohnePersonalnummer)],
  };
}
